import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Typography,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Box,
  Chip,
} from "@mui/material";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router-dom";
import { useUserQuery } from "../../../../hooks/api_hooks/useUserQuery";
import { useQueryClient } from "@tanstack/react-query";
import EditIcon from "@mui/icons-material/Edit";
import { EMPTY_FORM } from "./constants";
import {
  normalizeIncoming,
  trimAll,
  validateAll,
  safeParse,
  normalizePhone,
  isValidNanp,
} from "./validation";
import PersonalInfoReadView from "./PersonalInfoReadView";
import PersonalInfoEditForm from "./PersonalInfoEditForm";

export default function PersonalInformationForm() {
  useEffect(() => {
    document.title = "Personal Information | Kloudscan";
  }, []);

  const { user } = useAuth();
  const { data } = useUserQuery();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const userRecord = data?.user;
  const personalInfo = userRecord?.personal_information ?? EMPTY_FORM;

  const [initialData, setInitialData] = useState(EMPTY_FORM);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const firstLoad = useRef(true);
  const hasHydrated = useRef(false);

  useEffect(() => {
    if (userRecord) firstLoad.current = false;
  }, [userRecord]);

  useEffect(() => {
    if (userRecord && !hasHydrated.current) {
      const norm = normalizeIncoming(personalInfo, EMPTY_FORM);
      setInitialData(norm);
      setFormData(norm);
      if (Object.values(norm).every((v) => !v)) setEditing(true);
      hasHydrated.current = true;
    }
  }, [userRecord, personalInfo]);

  const isDirty = useMemo(
    () => JSON.stringify(formData) !== JSON.stringify(initialData),
    [formData, initialData],
  );

  /* ─── Save ─── */
  const onSave = async (e) => {
    e?.preventDefault?.();
    const trimmed = trimAll(formData);
    const validation = validateAll(trimmed);
    setFieldErrors(validation.errors);
    if (!validation.ok) return;

    setSaving(true);
    const previousUser = queryClient.getQueryData(["user"]);

    try {
      queryClient.setQueryData(["user"], (old) => {
        if (!old?.user) return old;
        return { ...old, user: { ...old.user, personal_information: trimmed } };
      });

      const res = await fetch("https://api.kloudscan.com/personal-information", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${user?.access_token ?? ""}`,
        },
        body: JSON.stringify(trimmed),
      });

      if (!res.ok) throw new Error(`Failed to save: ${res.status}`);

      const saved = safeParse(await res.text());
      const next = normalizeIncoming(saved, trimmed);

      setInitialData(next);
      setFormData(next);
      setEditing(false);

      queryClient.setQueryData(["user"], (old) => {
        if (!old?.user) return old;
        return { ...old, user: { ...old.user, personal_information: next } };
      });

      await queryClient.invalidateQueries({
        queryKey: ["user"],
        refetchType: "inactive",
      });

      window.scrollTo({ top: 0, behavior: "smooth" });
      setSnack({ open: true, message: "Changes saved successfully", severity: "success" });

      const isAuthorizationSigned = !!userRecord?.authorization_signed;
      setTimeout(() => {
        if (!isAuthorizationSigned)
          navigate("/profile/authorization", { replace: true });
      }, 1000);
    } catch (err) {
      console.error("Save failed:", err);
      if (previousUser) queryClient.setQueryData(["user"], previousUser);
      setSnack({ open: true, message: "Save failed — changes reverted", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  /* ─── Field handlers ─── */
  const onChange = (field) => (e) => {
    const val = e.target.value;
    setFormData((p) => ({ ...p, [field]: val }));
    if (fieldErrors[field]) setFieldErrors((f) => ({ ...f, [field]: "" }));
  };

  const onPhoneBlur = () => {
    const normalized = normalizePhone(formData.phoneNumber);
    const valid = isValidNanp(normalized);
    setFormData((p) => ({ ...p, phoneNumber: normalized }));
    if (!valid && normalized)
      setFieldErrors((f) => ({ ...f, phoneNumber: "Invalid phone number" }));
  };

  const onDateChange = (v) => {
    setFormData((p) => ({ ...p, birthday: v ? v.format("YYYY-MM-DD") : "" }));
    if (fieldErrors.birthday) setFieldErrors((f) => ({ ...f, birthday: "" }));
  };

  const onStateChange = (_, val) => {
    const code = val ? val.split("—")[0].trim() : "";
    setFormData((p) => ({ ...p, state: code }));
    if (fieldErrors.state) setFieldErrors((f) => ({ ...f, state: "" }));
  };

  const onEdit = () => setEditing(true);
  const onCancel = () => {
    setFormData(initialData);
    setFieldErrors({});
    setEditing(false);
  };

  if (!hasHydrated.current) {
    return (
      <Box
        sx={{
          minHeight: 300,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={32} sx={{ color: "#09b2f0" }} />
      </Box>
    );
  }

  return (
    <>
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: "10px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.14)",
            fontWeight: 600,
            fontSize: "0.875rem",
          }}
        >
          {snack.message}
        </Alert>
      </Snackbar>

      <Box
        sx={{
          backgroundColor: "#fff",
          borderRadius: "16px",
          border: "1px solid #E2E8F0",
          boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}
      >
        {/* Card Header */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #F1F5F9",
            backgroundColor: "#FAFBFD",
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: "#0F172A" }}>
              Personal Information
            </Typography>
            <Typography sx={{ fontSize: "0.78rem", color: "#94A3B8", mt: 0.25 }}>
              Your name, contact, and address details
            </Typography>
          </Box>

          {!editing ? (
            <Button
              onClick={onEdit}
              startIcon={<EditIcon sx={{ fontSize: "15px !important" }} />}
              size="small"
              sx={{
                backgroundColor: "#f59e0b",
                color: "#000",
                fontWeight: 700,
                fontSize: "0.78rem",
                textTransform: "none",
                borderRadius: "8px",
                px: 2,
                py: 0.75,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#f59e0b",
                  opacity: 0.85,
                  boxShadow: "none",
                },
              }}
            >
              Edit
            </Button>
          ) : (
            <Chip
              label="Editing"
              size="small"
              sx={{
                backgroundColor: "#FFF7ED",
                color: "#EA580C",
                fontWeight: 600,
                fontSize: "0.72rem",
                border: "1px solid #FED7AA",
                borderRadius: "6px",
                height: 26,
              }}
            />
          )}
        </Box>

        {!editing && <PersonalInfoReadView formData={formData} />}
        {editing && (
          <PersonalInfoEditForm
            formData={formData}
            fieldErrors={fieldErrors}
            onChange={onChange}
            onPhoneBlur={onPhoneBlur}
            onDateChange={onDateChange}
            onStateChange={onStateChange}
            onCancel={onCancel}
            onSave={onSave}
            saving={saving}
            isDirty={isDirty}
          />
        )}
      </Box>
    </>
  );
}
